import React, {ReactNode} from 'react';
import {Trans} from '@lingui/react';
import {t} from '@lingui/core/macro';

import ModalBlockButton from 'shared/ui/Button/ModalBlockButton/ModalBlockButton';
import ModalBodyWrapper from 'shared/ui/Modal/ModalBodyWrapper';
import UpgradeTariffButton from 'shared/ui/Button/UpgradeTariffButton/UpgradeTariffButton';
import ProgressBar from 'shared/ui/ProgressBar/ProgressBar';
import Correct from 'shared/ui/icons/General/Correct';
import Wrong from 'shared/ui/icons/General/Wrong';
import {isTranslate} from 'shared/lib/lang';

import {PALETTE} from 'shared/config/model/palette';

import {brandColor} from 'shared/lib/colors';

import IBatchResult from '../../model/IBatchResult';

import styles from './BatchResult.module.scss';

interface IBatchResultProps {
    result: IBatchResult,
    handleClose: () => void;
    title?: ReactNode;
    countLabel?: ReactNode;
}

const toErrorText = (error: unknown): string => {
    if (typeof error === 'string') {
        return error;
    }

    if (typeof error === 'number' || typeof error === 'boolean') {
        return String(error);
    }

    if (error && typeof error === 'object') {
        const message = (error as {message?: unknown}).message;

        return typeof message === 'string' ? message : '';
    }

    return '';
};

const normalizeErrors = (errors: unknown): string[] => {
    let list: unknown[] = [];

    if (Array.isArray(errors)) {
        list = errors;
    } else if (errors && typeof errors === 'object') {
        list = Object.values(errors as Record<string, unknown>);
    }

    return list.map(toErrorText).filter(error => error.length > 0);
};

const BatchResult: React.FC<IBatchResultProps> = ({result, handleClose, title, countLabel}) => {
    const errors = normalizeErrors(result.errors);
    const hasErrors = errors.length > 0;
    const showCloseButton = !result.show_tariff_button || isTranslate();

    return (
        <>
            <ModalBodyWrapper>
                {title && <h3 className={styles.title}>{title}</h3>}
                <div className={styles.statusRow}>
                    <span className={styles.statusIcon}>
                        <Correct color={brandColor(PALETTE.accent, PALETTE.success)}/>
                    </span>
                    <span className="text-medium">
                        {countLabel ?? <Trans id="batch.result.successful" message="Успешно"/>}: {result.success_count}
                    </span>
                </div>
                {hasErrors && (
                    <div className={styles.statusRow}>
                        <span className={styles.statusIcon}>
                            <Wrong />
                        </span>
                        <span className="text-medium">
                            <Trans id="batch.result.errors" message="Ошибок"/>: {errors.length}
                        </span>
                    </div>
                )}
                {hasErrors && (
                    <div className={styles.errorsBlock}>
                        <p className="text-medium">
                            <Trans id="batch.result.errors_text" message="Ошибки"/>
                        </p>
                        <ol className={styles.errorsList}>
                            {errors.map((error, index) => (
                                <li key={`${index}_${error}`}>{error}</li>
                            ))}
                        </ol>
                    </div>
                )}
            </ModalBodyWrapper>
            <ProgressBar percent={result.progress ?? 100} className={styles.progress}/>
            {result.show_tariff_button && !isTranslate() && <UpgradeTariffButton />}
            {showCloseButton && (
                <ModalBlockButton
                    primaryButtonText={t`Продолжить`}
                    primaryButtonType="button"
                    primaryButtonOnClick={handleClose}
                    secondaryButtonDisabled={true}
                />
            )}
        </>
    );
};

export default BatchResult;
